/**
 * Store: legacyAuth.js
 *
 * Gerencia a autenticação com a API legada (mobi-admin-ui).
 * Armazena sessão em localStorage (legacy_access_token, legacy_login, legacy_permissions).
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useNuxtApp } from 'nuxt/app'

export const useLegacyAuthStore = defineStore('legacyAuth', () => {
  const user = ref(null)
  const token = ref(null)
  const permissions = ref([])
  const error = ref(null)

  // Inicializa a partir do localStorage (persiste entre reloads)
  function init() {
    try {
      const storedToken = localStorage.getItem('legacy_access_token')
      const storedLogin = localStorage.getItem('legacy_login')
      const storedPerms = localStorage.getItem('legacy_permissions')

      if (storedToken) token.value = storedToken
      if (storedLogin) user.value = JSON.parse(atob(storedLogin))
      if (storedPerms) permissions.value = JSON.parse(atob(storedPerms))
    } catch (e) {
      // dados corrompidos — limpa
      clearSession()
    }
  }

  const isAuthenticated = computed(() => !!token.value)

  function clearSession() {
    user.value = null
    token.value = null
    permissions.value = []
    error.value = null
    localStorage.removeItem('legacy_access_token')
    localStorage.removeItem('legacy_login')
    localStorage.removeItem('legacy_permissions')
  }

  async function login(email, password) {
    const { $legacyApi } = useNuxtApp()
    const idEntity = localStorage.getItem('id_entity') || localStorage.getItem('legacy_id_entity')

    if (!idEntity) {
      throw new Error('id_entity não configurado para a API legada')
    }

    const response = await $legacyApi.post(`${idEntity}/oauth-login`, {
      email,
      password,
      app_version: 'web',
      locale: 'pt',
      id_entity: idEntity,
    })

    const data = response.data.data
    user.value = data
    token.value = data.access_token

    // Persiste em localStorage
    localStorage.setItem('legacy_access_token', data.access_token)
    localStorage.setItem('legacy_login', btoa(JSON.stringify(data)))
    localStorage.setItem('legacy_permissions', btoa(JSON.stringify(data.permissions || [])))
    permissions.value = data.permissions || []

    // Configura axios global para requisições legadas
    $legacyApi.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`

    return data
  }

  function logout() {
    clearSession()
  }

  function hasPermission(permission) {
    if (!permissions.value || permissions.value.length === 0) return false
    return permissions.value.includes(permission)
  }

  return {
    user,
    token,
    permissions,
    error,
    isAuthenticated,
    init,
    login,
    logout,
    clearSession,
    hasPermission,
  }
})
